// @ts-nocheck
import { Capacitor } from "@capacitor/core";
import axios from "axios";

import i18n from "@/i18n";
import { getAnonymousRequestContext } from "@/lib/demo";

const isNative = Capacitor.isNativePlatform();
const WEB_BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:8001" : "");
const BACKEND_URL = isNative ? process.env.REACT_APP_NATIVE_BACKEND_URL : WEB_BACKEND_URL;

if (!BACKEND_URL) {
  throw new Error(
    isNative
      ? "Missing REACT_APP_NATIVE_BACKEND_URL for native app runtime."
      : "Missing REACT_APP_BACKEND_URL for web runtime.",
  );
}

const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

let authTokenProvider = null;

// The review email links back with ?resume=<token>. Sent as a header, it reopens the
// visitor's anonymous progress in whatever browser the link opens in; the server then
// sets its cookie. Kept for the tab, and dropped from the URL so it isn't shared on.
const RESUME_KEY = "anon_resume_token";
const resumeToken = (() => {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("resume");
  if (fromUrl) {
    url.searchParams.delete("resume");
    window.history.replaceState(window.history.state, "", url.toString());
  }
  try {
    if (fromUrl) sessionStorage.setItem(RESUME_KEY, fromUrl);
    return fromUrl || sessionStorage.getItem(RESUME_KEY);
  } catch {
    return fromUrl;
  }
})();

apiClient.interceptors.request.use(async (config) => {
  const headers = config.headers ?? {};
  headers["Accept-Language"] = i18n.language || "en";

  let token = null;
  if (headers.Authorization) {
    token = headers.Authorization;
  } else if (authTokenProvider) {
    try {
      const resolved = await authTokenProvider();
      if (resolved) token = `Bearer ${resolved}`;
    } catch {
      token = null;
    }
  }

  if (token) {
    headers.Authorization = token;
    delete headers["X-Anonymous-Id"];
    delete headers["X-Anonymous-Fingerprint"];
    delete headers["X-Anonymous-Timezone"];
    delete headers["X-Anonymous-Language"];
    delete headers["X-Anonymous-Platform"];
    delete headers["X-Anonymous-Resume"];
  } else {
    const context = await getAnonymousRequestContext();
    headers["X-Anonymous-Id"] = context.anonymousId;
    headers["X-Anonymous-Fingerprint"] = context.fingerprint;
    headers["X-Anonymous-Timezone"] = context.timezone;
    headers["X-Anonymous-Language"] = context.language;
    headers["X-Anonymous-Platform"] = context.platform;
    if (resumeToken) headers["X-Anonymous-Resume"] = resumeToken;
  }

  config.headers = headers;
  return config;
});

export const setAuthTokenProvider = (provider) => {
  authTokenProvider = typeof provider === "function" ? provider : null;
};

const localTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

// API functions
export const api = {
  // Health check
  health: () => apiClient.get("/health"),

  getPublicFeatureFlags: () => apiClient.get("/feature-flags/public"),

  // Trial
  getTrialStatus: () => apiClient.get("/account/trial/status"),
  getAccountLimits: () => apiClient.get("/account/limits"),

  completeTrial: () => apiClient.post("/account/trial/complete"),

  claimAnonymousData: () => apiClient.post("/account/claim-anonymous"),

  saveReminderEmail: (email) => apiClient.post("/account/reminder-email", { email }),
  getExamPlan: () => apiClient.get("/account/exam-plan", { params: { timezone: localTimezone() } }),
  saveExamDate: (examDate) =>
    apiClient.put("/account/exam-date", { exam_date: examDate, timezone: localTimezone() }),

  // Books
  uploadBook: async (file, title) => {
    const formData = new FormData();
    formData.append("file", file);
    if (title) {
      formData.append("title", title);
    }
    return apiClient.post("/books/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  getBooks: () => apiClient.get("/books"),

  getSampleBooks: () => apiClient.get("/books/sample-books"),

  getBook: (bookId) => apiClient.get(`/books/${bookId}`),

  scopeBook: (bookId, chapterIndices) =>
    apiClient.post(`/books/${bookId}/scope`, { chapter_indices: chapterIndices }),

  getBookFile: (bookId) =>
    apiClient.get(`/books/${bookId}/file`, {
      responseType: "blob",
    }),

  deleteBook: (bookId) => apiClient.delete(`/books/${bookId}`),

  // Quiz
  generateQuiz: (bookId, count = 5, chapterIndices = null, mock = false) =>
    apiClient.post("/quizzes/generate", {
      book_id: bookId,
      count,
      ...(chapterIndices && { chapter_indices: chapterIndices }),
      ...(mock && { mode: "mock" }),
    }),
  createSharedQuiz: (bookId, questionIds) =>
    apiClient.post("/quizzes/share", { book_id: bookId, question_ids: questionIds }),
  getSharedQuiz: (shareId) => apiClient.get(`/quizzes/shared/${shareId}`),
  submitSharedQuiz: (shareId, answers) =>
    apiClient.post(`/quizzes/shared/${shareId}/submit`, { answers }),

  submitAnswer: (questionId, selectedAnswer, streak = 0, elapsedMs = null) =>
    apiClient.post("/quizzes/answer", {
      question_id: questionId,
      selected_answer: selectedAnswer,
      streak,
      elapsed_ms: elapsedMs,
    }),
  setQuizDifficulty: (payload) => apiClient.post("/quizzes/difficulty", payload),

  askWhy: (questionId, selectedAnswer, message) =>
    apiClient.post("/quizzes/ask-why", {
      question_id: questionId,
      selected_answer: selectedAnswer,
      message,
    }),

  reportQuestion: (questionId, reason = "other", details = "") =>
    apiClient.post("/quizzes/feedback", {
      question_id: questionId,
      reason,
      details,
    }),

  // Flashcards
  getFlashcardDeck: (bookId, count = 12, chapterIndices = null) =>
    apiClient.post("/flashcards/deck", {
      book_id: bookId,
      count,
      ...(chapterIndices && { chapter_indices: chapterIndices }),
    }),

  reviewFlashcard: (questionId, gotIt) =>
    apiClient.post("/flashcards/review", { question_id: questionId, got_it: gotIt }),

  unlockQuizExtras: () => apiClient.post("/quizzes/unlock-extras"),

  startAdUnlock: () => apiClient.post("/quizzes/ad-unlock/start"),
  claimAdUnlock: (nonce) => apiClient.post("/quizzes/ad-unlock/claim", { nonce }),

  // Progress
  getAllProgress: () => apiClient.get("/progress"),

  getBookProgress: (bookId) => apiClient.get(`/progress/${bookId}`),

  getStats: () => apiClient.get("/stats"),

  // Concepts
  getBookConcepts: (bookId) => apiClient.get(`/books/${bookId}/concepts`),
  extractBookConcepts: (bookId) => apiClient.post(`/books/${bookId}/extract-concepts`),

  // Mastery
  getBookMastery: (bookId) => apiClient.get(`/books/${bookId}/mastery`),

  // Study plan (active chapter + per-chapter mastery for auto-continue flow)
  getStudyPlan: (bookId) => apiClient.get(`/books/${bookId}/study-plan`),

  // Review scheduling
  getDueForReview: () => apiClient.get("/books/due-for-review"),
  getReviewSchedule: () => apiClient.get("/progress/review-schedule"),

  // Credits
  getCreditBalance: () => apiClient.get("/account/credits/balance"),
  getBillingPrices: () => apiClient.get("/billing/prices"),
  createCreditCheckout: (pack) => apiClient.post("/billing/credits/checkout", { pack }),
  verifyCreditSession: (sessionId) =>
    apiClient.post("/billing/credits/verify-session", { session_id: sessionId }),
  createProCheckout: () => apiClient.post("/billing/pro/checkout"),
  createBillingPortal: () => apiClient.post("/billing/portal"),
  getSubscription: () => apiClient.get("/billing/subscription"),
  cancelSubscription: () => apiClient.post("/billing/subscription/cancel"),

  // Topics (public quiz pages)
  resolveTopic: (query, entry = "typed") =>
    apiClient.post("/topics/resolve", {
      query,
      entry,
      language: i18n.resolvedLanguage || "en",
    }),
  getTopic: (slug) => apiClient.get(`/topics/${slug}`),

  // Referrals
  claimReferral: (shareId) => apiClient.post("/referrals/claim", { share_id: shareId }),
};

export const getApiErrorDetail = (error) => error?.response?.data?.error;

export const getApiErrorCode = (error) => {
  const detail = getApiErrorDetail(error);
  if (typeof detail === "object" && detail !== null && detail.code) return detail.code;
  return null;
};

export const getApiErrorMessage = (error, fallback = "Something went wrong.") => {
  const detail = getApiErrorDetail(error);
  if (typeof detail === "object" && detail?.message) return detail.message;
  if (typeof detail === "string") return detail;
  return fallback;
};

export const isSignupRequiredError = (error) => getApiErrorCode(error) === "SIGNUP_REQUIRED";
export const isDailyQuizLimitError = (error) =>
  getApiErrorCode(error) === "DAILY_QUIZ_LIMIT_REACHED";
export const getDailyQuizLimitDetails = (error) => {
  const detail = getApiErrorDetail(error);
  if (typeof detail === "object" && detail !== null) return detail.details || {};
  return {};
};
export const isAdUnlockLimitError = (error) => getApiErrorCode(error) === "AD_UNLOCK_LIMIT_REACHED";
export const isInsufficientCreditsError = (error) =>
  getApiErrorCode(error) === "INSUFFICIENT_CREDITS";
export const isQuizShareLimitError = (error) =>
  getApiErrorCode(error) === "QUIZ_SHARE_LIMIT_REACHED";
export const isSharedQuizAlreadyUsedError = (error) =>
  getApiErrorCode(error) === "SHARED_QUIZ_ALREADY_USED";
export const isSharedQuizNotFoundError = (error) =>
  getApiErrorCode(error) === "SHARED_QUIZ_NOT_FOUND";

export default api;

// Scoping re-indexes the book in the background and answers while it is still
// "indexing"; asking for a quiz or deck right away got "Book is not ready for
// quizzing" (prod 2026-10-06, ready 10s later). Capped at 2 minutes, after which the
// caller's own request reports the real state.
export async function waitForBookIndexed(bookId, status) {
  for (let i = 0; status === "indexing" && i < 60; i++) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    status = (await api.getBook(bookId)).data.status;
  }
}
